import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Copy, Save, Trash2, ArrowLeft, Plus } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Loading } from "@/components/common/Common";
import { useConfirm } from "@/components/common/useConfirm";
import { useAuth } from "@/context/AuthContext";
import { CampStatus } from "@/pages/Campaigns";
import { KeywordsTab, NegativesTab } from "@/pages/campaign/KeywordTabs";
import { AdsTab, AssetsTab } from "@/pages/campaign/AdTabs";
import ReviewTab from "@/pages/campaign/ReviewTab";
import { api, errMsg } from "@/lib/api";

function DuplicateDialog({ c, open, onClose }) {
  const nav = useNavigate();
  const [rows, setRows] = useState([{ city: "", state: c.business.state || "", daily_budget: "", service: "" }]);
  const run = async () => {
    try {
      const variants = rows.filter((r) => r.city || r.service || r.daily_budget).map((r) => ({ city: r.city || null, state: r.state || null, service: r.service || null, daily_budget: r.daily_budget ? Number(r.daily_budget) : null }));
      const { data } = await api.post(`/campaigns/${c.id}/duplicate`, { variants });
      toast.success(`${data.created.length} campanha(s) criada(s) como rascunho`);
      onClose();
      nav("/app/campaigns");
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="duplicate-dialog">
        <DialogHeader><DialogTitle>Duplicar campanha</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Mantém a mesma estrutura. Informe nova cidade, novo serviço e/ou novo orçamento. Nomes, palavras-chave e anúncios são ajustados automaticamente.</p>
        <div className="space-y-2">
          {rows.map((r, i) => (
            <div key={i} className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <Input placeholder="Nova cidade" value={r.city} onChange={(e) => setRows(rows.map((x, j) => j === i ? { ...x, city: e.target.value } : x))} data-testid={`dup-city-${i}`} />
              <Input placeholder="UF" value={r.state} onChange={(e) => setRows(rows.map((x, j) => j === i ? { ...x, state: e.target.value } : x))} />
              <Input placeholder="Novo serviço" value={r.service} onChange={(e) => setRows(rows.map((x, j) => j === i ? { ...x, service: e.target.value } : x))} data-testid={`dup-service-${i}`} />
              <Input placeholder="R$/dia" type="number" value={r.daily_budget} onChange={(e) => setRows(rows.map((x, j) => j === i ? { ...x, daily_budget: e.target.value } : x))} data-testid={`dup-budget-${i}`} />
            </div>
          ))}
          <Button variant="ghost" size="sm" onClick={() => setRows([...rows, { city: "", state: c.business.state || "", daily_budget: "", service: "" }])} data-testid="dup-add-row"><Plus className="h-3 w-3 mr-1" />Outra variação</Button>
        </div>
        <DialogFooter><Button variant="ghost" onClick={onClose}>Cancelar</Button><Button onClick={run} data-testid="dup-confirm-button">Duplicar</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function CampaignEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [c, setC0] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [dup, setDup] = useState(false);
  const [confirm, dialog] = useConfirm();
  const load = useCallback(async () => { try { setC0((await api.get(`/campaigns/${id}`)).data); setDirty(false); } catch (e) { toast.error(errMsg(e)); nav("/app/campaigns"); } }, [id, nav]);
  useEffect(() => { load(); }, [load]);
  const setC = (v) => { setC0(v); setDirty(true); };
  const save = async (extra = {}) => {
    const body = { name: c.name, groups: c.groups, negatives: c.negatives, assets: c.assets, ...extra };
    const { data } = await api.put(`/campaigns/${id}`, body);
    setC0(data); setDirty(false); toast.success("Alterações salvas");
  };
  if (!c) return <Loading />;
  const ro = ["published", "publishing"].includes(c.status);
  const expert = !!user?.expert_mode;
  const props = { c, setC, ro, expert };
  const del = async () => { if (await confirm({ title: "Excluir campanha?", desc: ro ? "Remove apenas o registro na plataforma. A campanha continua existindo no Google Ads." : "O rascunho será removido.", danger: true, ok: "Excluir" })) { await api.delete(`/campaigns/${id}`); nav("/app/campaigns"); } };

  return (
    <div>
      {dialog}
      <DuplicateDialog c={c} open={dup} onClose={() => setDup(false)} />
      <Link to="/app/campaigns" className="text-xs text-muted-foreground inline-flex items-center gap-1 mb-4"><ArrowLeft className="h-3 w-3" />Campanhas</Link>
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-8">
        <Input value={c.name} disabled={ro} onChange={(e) => setC({ ...c, name: e.target.value })} className="text-xl font-semibold h-12 max-w-xl border-transparent hover:border-border" data-testid="campaign-name-input" />
        <CampStatus s={c.status} />
        <div className="sm:ml-auto flex gap-2">
          <Button variant="outline" className="rounded-full" onClick={() => setDup(true)} data-testid="duplicate-campaign-button"><Copy className="h-4 w-4 mr-1" />Duplicar campanha</Button>
          {!ro && <Button className="rounded-full" disabled={!dirty} onClick={() => save().catch((e) => toast.error(errMsg(e)))} data-testid="save-campaign-button"><Save className="h-4 w-4 mr-1" />Salvar</Button>}
          <Button variant="ghost" size="icon" onClick={del} data-testid="delete-campaign-button"><Trash2 className="h-4 w-4" /></Button>
        </div>
      </div>
      {ro && <p className="text-sm text-muted-foreground mb-6">Campanha publicada — somente leitura. Otimize pelo IA Optimizer.</p>}
      <Tabs defaultValue="keywords">
        <TabsList className="rounded-full mb-8 flex-wrap h-auto">
          <TabsTrigger value="keywords" className="rounded-full" data-testid="tab-keywords">06 · Palavras-chave</TabsTrigger>
          <TabsTrigger value="negatives" className="rounded-full" data-testid="tab-negatives">Negativas {c.conflicts?.length ? `(${c.conflicts.length} conflitos)` : ""}</TabsTrigger>
          <TabsTrigger value="ads" className="rounded-full" data-testid="tab-ads">07 · Anúncios</TabsTrigger>
          <TabsTrigger value="assets" className="rounded-full" data-testid="tab-assets">Extensões</TabsTrigger>
          <TabsTrigger value="review" className="rounded-full" data-testid="tab-review">08–09 · Revisão e publicação</TabsTrigger>
        </TabsList>
        <TabsContent value="keywords"><KeywordsTab {...props} /></TabsContent>
        <TabsContent value="negatives"><NegativesTab {...props} onDetect={() => save().catch((e) => toast.error(errMsg(e)))} /></TabsContent>
        <TabsContent value="ads"><AdsTab {...props} /></TabsContent>
        <TabsContent value="assets"><AssetsTab {...props} /></TabsContent>
        <TabsContent value="review"><ReviewTab c={c} reload={load} dirty={dirty} save={save} /></TabsContent>
      </Tabs>
    </div>
  );
}
